import React from 'react';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PlaceIcon from '@mui/icons-material/Place';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { SubscriptionTier } from '../../hooks/useTierAccess';

// Lazy load components for code splitting
const DashboardPage = React.lazy(() => import('../dashboard').then(m => ({ default: m.DashboardPage })));
const POSPage = React.lazy(() => import('../pos').then(m => ({ default: m.POSPage })));
const OrdersList = React.lazy(() => import('../orders').then(m => ({ default: m.OrdersList })));
const LocationList = React.lazy(() => import('../locations').then(m => ({ default: m.LocationList })));
const ProductList = React.lazy(() => import('../products/list').then(m => ({ default: m.ProductList })));
const InventoryPage = React.lazy(() => import('../inventory').then(m => ({ default: m.InventoryPage })));
const StockTransferList = React.lazy(() => import('../stock-transfers').then(m => ({ default: m.StockTransferList })));
const QuoteList = React.lazy(() => import('../quotes').then(m => ({ default: m.QuoteList })));
const InvoiceList = React.lazy(() => import('../invoices').then(m => ({ default: m.InvoiceList })));
const StaffList = React.lazy(() => import('../staff/list').then(m => ({ default: m.StaffList })));
const ResellerList = React.lazy(() => import('../resellers/list').then(m => ({ default: m.ResellerList })));
const AccessRequestList = React.lazy(() => import('../access-requests/list').then(m => ({ default: m.AccessRequestList })));
const PricingTierList = React.lazy(() => import('../pricing-tiers').then(m => ({ default: m.PricingTierList })));

export interface SectionTab {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  icon: React.ReactElement;
  flatRoute: string; // Route used in flat mode
  requiredTier?: SubscriptionTier;
  requiresB2B?: boolean;
  hasNestedTabs?: boolean; // Pages that have their own internal tabs
}

export interface SectionConfig {
  id: string;
  label: string;
  tabs: SectionTab[];
}

export const sections: SectionConfig[] = [
  {
    id: 'operations',
    label: 'OPERATIONS',
    tabs: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        component: DashboardPage,
        icon: React.createElement(DashboardOutlinedIcon),
        flatRoute: '/app',
        hasNestedTabs: true, // Overview | Analytics | B2B Partners
      },
      {
        id: 'pos',
        label: 'Point of Sale',
        component: POSPage,
        icon: React.createElement(PointOfSaleOutlinedIcon),
        flatRoute: '/app/pos',
      },
      {
        id: 'orders',
        label: 'Orders',
        component: OrdersList,
        icon: React.createElement(ShoppingCartOutlinedIcon),
        flatRoute: '/app/orders',
      },
      {
        id: 'locations',
        label: 'Locations',
        component: LocationList,
        icon: React.createElement(PlaceIcon),
        flatRoute: '/app/locations',
      },
    ],
  },
  {
    id: 'catalog',
    label: 'CATALOG & STOCK',
    tabs: [
      {
        id: 'products',
        label: 'Products',
        component: ProductList,
        icon: React.createElement(CategoryOutlinedIcon),
        flatRoute: '/app/products',
        hasNestedTabs: true, // All Products | Storefront | B2B Catalog
      },
      {
        id: 'inventory',
        label: 'Inventory',
        component: InventoryPage,
        icon: React.createElement(Inventory2OutlinedIcon),
        flatRoute: '/app/inventory',
        hasNestedTabs: true, // Stock Levels | Movements | Forecast
      },
      {
        id: 'stock-transfers',
        label: 'Stock Transfers',
        component: StockTransferList,
        icon: React.createElement(LocalShippingIcon),
        flatRoute: '/app/stock-transfers',
      },
    ],
  },
  {
    id: 'sales',
    label: 'SALES & QUOTES',
    tabs: [
      {
        id: 'sales',
        label: 'Sales History',
        component: OrdersList,
        icon: React.createElement(ReceiptLongOutlinedIcon),
        flatRoute: '/app/orders',
      },
      {
        id: 'quotes',
        label: 'Quotes',
        component: QuoteList,
        icon: React.createElement(RequestQuoteOutlinedIcon),
        flatRoute: '/app/quotes',
        requiresB2B: true,
      },
      {
        id: 'invoices',
        label: 'Invoices',
        component: InvoiceList,
        icon: React.createElement(ReceiptOutlinedIcon),
        flatRoute: '/app/invoices',
        requiresB2B: true,
      },
    ],
  },
  {
    id: 'team',
    label: 'TEAM & PARTNERS',
    tabs: [
      {
        id: 'staff',
        label: 'Staff',
        component: StaffList,
        icon: React.createElement(PeopleOutlinedIcon),
        flatRoute: '/app/staff',
      },
      {
        id: 'resellers',
        label: 'Resellers',
        component: ResellerList,
        icon: React.createElement(GroupOutlinedIcon),
        flatRoute: '/app/resellers',
        requiresB2B: true,
      },
      {
        id: 'access-requests',
        label: 'Access Requests',
        component: AccessRequestList,
        icon: React.createElement(PersonAddOutlinedIcon),
        flatRoute: '/app/access-requests',
        requiresB2B: true,
      },
      {
        id: 'pricing-tiers',
        label: 'Pricing Tiers',
        component: PricingTierList,
        icon: React.createElement(LocalOfferOutlinedIcon),
        flatRoute: '/app/pricing-tiers',
        requiresB2B: true,
      },
    ],
  },
];

// Helper function to get section by ID
export const getSectionById = (sectionId: string): SectionConfig | undefined => {
  return sections.find((section) => section.id === sectionId);
};

// Helper function to get a tab within a section
export const getTabInSection = (sectionId: string, tabId: string): SectionTab | undefined => {
  const section = getSectionById(sectionId);
  if (!section) return undefined;
  return section.tabs.find((tab) => tab.id === tabId);
};

// Helper function to find which section contains a flat route
export const findSectionByFlatRoute = (flatRoute: string): { section: SectionConfig; tab: SectionTab } | undefined => {
  for (const section of sections) {
    const tab = section.tabs.find((t) => t.flatRoute === flatRoute);
    if (tab) {
      return { section, tab };
    }
  }
  return undefined;
};

// Helper to get section route for tabs mode
export const getSectionRoute = (sectionId: string, tabId?: string): string => {
  const baseRoute = `/app/section/${sectionId}`;
  if (tabId) {
    return `${baseRoute}?tab=${tabId}`;
  }
  return baseRoute;
};
